/**
 * Navigation Component
 * Responsive top navigation bar with user authentication
 */

import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  DocumentChartBarIcon,
  UsersIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { useAuth, UserRole } from '../auth';
import { canAccessInsurance, canAccessDashboard, canAccessAIChat, canAccessReports, canAccessNutSheet, canViewConfig, canUseAgentAccess } from '../auth/permissions';
import { AIChatButton } from './ai-chat/AIChatButton';
import { FEATURE_FLAGS } from '../config/featureFlags';

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  requiredRole?: UserRole;
  // Special visibility flags (not based on role hierarchy)
  insuranceOnly?: boolean;  // Only show to Insurance, Admin, Executive
  dashboardOnly?: boolean;  // Hide from Insurance-only users
}

// Dashboard navigation items (hidden from Insurance-only users)
const dashboardNavItems: NavItem[] = [
  { name: 'Nut Sheet', path: '/nut-sheet', icon: ClipboardDocumentListIcon, requiredRole: 'Executive', dashboardOnly: true },
  { name: 'Forecast', path: '/forecast', icon: ChartBarIcon, dashboardOnly: true },
  { name: 'Historical Data', path: '/historical', icon: ClockIcon, requiredRole: 'GeneralManager', dashboardOnly: true },
  { name: 'Projections', path: '/projections', icon: ArrowTrendingUpIcon, dashboardOnly: true },
  { name: 'Reports', path: '/reports', icon: DocumentChartBarIcon, dashboardOnly: true },
  { name: 'Users', path: '/users', icon: UsersIcon, requiredRole: 'Executive', dashboardOnly: true },
  { name: 'Configuration', path: '/configuration', icon: Cog6ToothIcon, requiredRole: 'GeneralManager', dashboardOnly: true },
  { name: 'Messages', path: '/messages', icon: EnvelopeIcon, dashboardOnly: true },
];

// Insurance nav item (visible to Insurance, Admin, Executive)
const insuranceNavItem: NavItem = {
  name: 'Insurance',
  path: '/insurance',
  icon: ShieldCheckIcon,
  insuranceOnly: true,
};

// Role display names and colors
const roleConfig: Record<UserRole, { label: string; className: string }> = {
  Admin: { label: 'Admin', className: 'bg-red-500 text-white' },
  CEO: { label: 'CEO', className: 'bg-purple-700 text-white' },
  Executive: { label: 'Executive', className: 'bg-purple-500 text-white' },
  GeneralManager: { label: 'GM', className: 'bg-blue-500 text-white' },
  DepartmentManager: { label: 'Dept Mgr', className: 'bg-green-500 text-white' },
  TeamServiceManager: { label: 'TSM', className: 'bg-orange-500 text-white' },
  Insurance: { label: 'Insurance', className: 'bg-teal-500 text-white' },
  MarketingManager: { label: 'Marketing', className: 'bg-pink-500 text-white' },
  Accounting: { label: 'Accounting', className: 'bg-indigo-500 text-white' },
  FinanceManager: { label: 'F&I Mgr', className: 'bg-cyan-600 text-white' },
  Unassigned: { label: 'Pending', className: 'bg-yellow-500 text-gray-900' },
};

interface NavigationProps {
  userName?: string;
  userRole?: UserRole;
  isAIChatOpen?: boolean;
  onToggleAIChat?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({
  userName = 'Guest',
  userRole = 'Unassigned',
  isAIChatOpen = false,
  onToggleAIChat,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { logout, hasRole, assignedDepartmentIds } = useAuth();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    await logout();
  };

  // Filter nav items based on user role and special visibility flags
  const visibleNavItems = [
    // Dashboard items (hidden from Insurance-only users; MarketingManager sees Forecast + Reports only)
    ...dashboardNavItems.filter((item) => {
      // Nut Sheet has its own access rule (GM / DeptMgr w/ NEW or USED / Accounting),
      // not the role hierarchy — evaluate it before the requiredRole gate.
      if (item.path === '/nut-sheet') return canAccessNutSheet(userRole, assignedDepartmentIds);
      // Configuration likewise: GM+ per hierarchy, plus NEW-dept DeptMgrs
      // (who see only the Sales Teams tab on the page itself).
      if (item.path === '/configuration') return canViewConfig(userRole, assignedDepartmentIds);
      // Accounting and FinanceManager (F&I) are single-page roles: only the Nut Sheet item
      // (handled above) is visible.
      if (userRole === 'Accounting' || userRole === 'FinanceManager') return false;
      // Check role hierarchy requirement
      if (item.requiredRole && !hasRole(item.requiredRole)) return false;
      // MarketingManager can see Forecast and Reports pages
      if (userRole === 'MarketingManager') return item.path === '/reports' || item.path === '/forecast';
      // Hide dashboard items from Insurance-only users
      if (item.dashboardOnly && !canAccessDashboard(userRole)) return false;
      // TSM can see the Forecast page and the Reports page (Reports is limited to the
      // TSM Group Ranking + TSM Items reports by reportDefinitions allowedRoles).
      if (userRole === 'TeamServiceManager' && item.path !== '/forecast' && item.path !== '/reports') return false;
      return true;
    }),
    // Insurance item (only for Insurance, Admin, Executive)
    ...(canAccessInsurance(userRole) ? [insuranceNavItem] : []),
  ];

  const roleInfo = roleConfig[userRole];

  return (
    <nav className="bg-slate-800 shadow-lg sticky top-0 z-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Brand */}
          <div className="flex-shrink-0">
            <NavLink to="/" className="flex items-center space-x-2">
              <ChartBarIcon className="h-8 w-8 text-blue-400" />
              <span className="text-white font-bold text-lg hidden sm:block">
                Darling's Dashboard
              </span>
            </NavLink>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-1">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`
                }
              >
                <item.icon className="h-5 w-5 mr-1.5" />
                <span>{item.name}</span>
              </NavLink>
            ))}
          </div>

          {/* User Welcome - Desktop */}
          <div className="hidden lg:flex lg:items-center lg:space-x-4 relative">
            {/* AI Chat Button */}
            {FEATURE_FLAGS.AI_CHAT_ENABLED && canAccessAIChat(userRole) && onToggleAIChat && (
              <AIChatButton onClick={onToggleAIChat} isOpen={isAIChatOpen} />
            )}

            <button
              onClick={toggleUserMenu}
              className="flex items-center space-x-2 text-slate-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-700"
            >
              <UserCircleIcon className="h-8 w-8 text-blue-400" />
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium text-white">{userName}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${roleInfo.className}`}>
                  {roleInfo.label}
                </span>
              </div>
            </button>

            {/* User Dropdown Menu */}
            {isUserMenuOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsUserMenuOpen(false)}
                />
                {/* Dropdown */}
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{userName}</p>
                    <p className="text-xs text-gray-500">{roleInfo.label}</p>
                  </div>
                  {canUseAgentAccess(userRole) && (
                    <NavLink
                      to="/agent-access"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <KeyIcon className="h-5 w-5 mr-2 text-gray-400" />
                      Agent Access
                    </NavLink>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2 text-gray-400" />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center space-x-3">
            {/* AI Chat Button - Mobile */}
            {FEATURE_FLAGS.AI_CHAT_ENABLED && canAccessAIChat(userRole) && onToggleAIChat && (
              <AIChatButton onClick={onToggleAIChat} isOpen={isAIChatOpen} />
            )}

            {/* User Welcome - Mobile (compact) */}
            <button
              onClick={toggleUserMenu}
              className="flex items-center space-x-1 text-slate-300"
            >
              <UserCircleIcon className="h-6 w-6 text-blue-400" />
              <span className="text-sm font-medium text-white max-w-[100px] truncate">
                {userName}
              </span>
            </button>

            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={`lg:hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen
            ? 'max-h-screen opacity-100'
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 bg-slate-800 border-t border-slate-700">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `flex items-center px-3 py-3 rounded-md text-base font-medium transition-colors duration-200 ${
                  isActive
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`
              }
            >
              <item.icon className="h-6 w-6 mr-3" />
              <span>{item.name}</span>
            </NavLink>
          ))}

          {/* Mobile Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-3 py-3 rounded-md text-base font-medium text-red-400 hover:bg-slate-700 hover:text-red-300"
          >
            <ArrowRightOnRectangleIcon className="h-6 w-6 mr-3" />
            <span>Sign out</span>
          </button>
        </div>
      </div>

      {/* Mobile User Dropdown */}
      {isUserMenuOpen && (
        <div className="lg:hidden">
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsUserMenuOpen(false)}
          />
          <div className="absolute right-4 top-16 w-48 bg-white rounded-md shadow-lg py-1 z-20">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">{userName}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${roleInfo.className}`}>
                {roleInfo.label}
              </span>
            </div>
            {canUseAgentAccess(userRole) && (
              <NavLink
                to="/agent-access"
                onClick={() => setIsUserMenuOpen(false)}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <KeyIcon className="h-5 w-5 mr-2 text-gray-400" />
                Agent Access
              </NavLink>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2 text-gray-400" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};
